import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { ButtonGroup } from '@/design-system/atoms/ButtonGroup';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { Image } from '@/design-system/atoms/Image';
import { RichTextEditor } from '@/design-system/atoms/RichTextEditor';
import { useIsChild } from '@/design-system/contexts/ContentTreeContext/ContentTreeContext';

import styles from './CopyBlock.module.scss';

/**
 * The Copy Block Component is used to generates static copy and provides
 * authors the ability to add large copy blocks onto the page.
 *
 * The Copy Block can be displayed in two different widths, inset and full width.
 */
const getAnalyticsComponentTitle = (text) => {
    let title = '';
    if (text) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const titleNodes = doc.querySelectorAll('h2, h3, h4, h5, h6');
        if (titleNodes.length > 0) {
            title = titleNodes[0].textContent;
        }
    }
    return title;
};
const CopyBlock = React.forwardRef((props, ref) => {
    const {
        className,
        text,
        ctas,
        contentWidth,
        image,
        appliedCssClassNames,
        isToppedAlign,
        isRightAlign,
        isLeftAlign,
        isLargeImage,
        showAsset,
        pageTemplate = '',
        hasPlainTable,
        hasStripedTable,
        hasBorderedTable,
        hasCompactTable,
        ...otherProps
    } = props;
    const isArticleTemplate =
        pageTemplate.toLowerCase().replaceAll(' ', '-') === 'article-template';
    const isInset = contentWidth === 'inset';
    const isChild = useIsChild();
    const [analyticsComponentTitle, setAnalyticsComponentTitle] = React.useState('');

    React.useEffect(() => {
        setAnalyticsComponentTitle(getAnalyticsComponentTitle(text));
    }, [text]);

    return (
        <ContentContainer isFullWidth={isChild}>
            <div
                ref={ref}
                className={classnames(
                    styles['copy-block'],
                    className,
                    hasPlainTable ? styles.tableStyle_plain : undefined,
                    hasStripedTable ? styles.tableStyle_striped : undefined,
                    hasBorderedTable ? styles.tableStyle_bordered : undefined,
                    hasCompactTable ? styles.tableStyle_compact : undefined,
                )}
                {...otherProps}
            >
                <Grid>
                    <GridColumn
                        colSpan={{
                            sm: 4,
                            md: 6,
                            lg: isInset ? 6 : 12,
                        }}
                    >
                        {showAsset ? (
                            <div
                                className={classnames(
                                    isToppedAlign ? styles['copy-block__image_top'] : undefined,
                                    styles['copy-block__wrapper'],
                                )}
                            >
                                <div
                                    className={classnames(
                                        isLeftAlign ? styles['copy-block__image_left'] : undefined,
                                        isRightAlign
                                            ? styles['copy-block__image_right']
                                            : undefined,
                                        isToppedAlign
                                            ? styles['copy-block__image_align-top']
                                            : undefined,
                                        isLargeImage
                                            ? styles['copy-block__image_large']
                                            : undefined,
                                        styles['copy-block__image'],
                                    )}
                                >
                                    <Image {...image} roundedCorners />
                                </div>
                                <RichTextEditor
                                    isTypeH3={isArticleTemplate}
                                    text={text}
                                    withUtmParam
                                    analytics={{
                                        componentTitle: analyticsComponentTitle,
                                        componentName: 'Copy Block',
                                    }}
                                />
                            </div>
                        ) : (
                            <RichTextEditor
                                isTypeH3={isArticleTemplate}
                                text={text}
                                withUtmParam
                                analytics={{
                                    componentTitle: analyticsComponentTitle,
                                    componentName: 'Copy Block',
                                }}
                            />
                        )}
                        {ctas && (
                            <div className={styles['copy-block__actions']}>
                                <ButtonGroup
                                    buttons={ctas}
                                    analytics={{
                                        componentTitle: analyticsComponentTitle,
                                        componentName: 'Copy Block',
                                    }}
                                />
                            </div>
                        )}
                    </GridColumn>
                </Grid>
            </div>
        </ContentContainer>
    );
});

CopyBlock.displayName = 'CopyBlock';

/**
 * Content width options
 */
CopyBlock.CONTENT_WIDTH = {
    INSET: 'inset',
    FULL_WIDTH: 'fullWidth',
};

CopyBlock.propTypes = {
    /**
     * HTML content
     */
    text: PropTypes.string,
    /**
     * Array of links
     */
    ctas: PropTypes.arrayOf(PropTypes.shape(Button.prototype)),
    /**
     * Width of the content in the grid
     */
    contentWidth: PropTypes.oneOf(['inset', 'fullWidth']),
    /**
     * Apply plain table style (removes default striped rows)
     */
    hasPlainTable: PropTypes.bool,
    /**
     * Apply striped table style (explicit striped rows)
     */
    hasStripedTable: PropTypes.bool,
    /**
     * Apply bordered table style
     */
    hasBorderedTable: PropTypes.bool,
    /**
     * Apply compact table style (tighter spacing)
     */
    hasCompactTable: PropTypes.bool,
};

export default CopyBlock;
